import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Navigation from '../../components/feature/Navigation';

export default function Checkout() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const plan = searchParams.get('plan') || 'free';
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const planDetails = {
    free: {
      name: 'Free',
      price: 0,
      type: 'one-time',
      features: [
        '5 message analyses per day',
        'Basic tone analysis',
        '3 reply suggestions',
        'Red flag detection',
        'Access to learning lessons'
      ]
    },
    premium: {
      name: 'Premium',
      price: 5,
      type: 'subscription',
      features: [
        'Unlimited message analyses',
        'Advanced tone & intent analysis',
        '5+ reply suggestions per message',
        'Detailed red flag explanations',
        'Exclusive learning content',
        'Priority support'
      ]
    }
  };

  const currentPlan = planDetails[plan as keyof typeof planDetails] || planDetails.free;

  const handleCheckout = async () => {
    setIsProcessing(true);
    setError(null);

    try {
      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
      
      if (!supabaseUrl) {
        throw new Error('Configuration error. Please try again.');
      }

      if (plan === 'free') {
        // For free plan, just redirect to signup/dashboard
        navigate('/signup');
        return;
      }

      // For premium plan, create Stripe checkout session
      const response = await fetch(`${supabaseUrl}/functions/v1/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          plan: 'premium',
          successUrl: `${window.location.origin}/dashboard?session_id={CHECKOUT_SESSION_ID}`,
          cancelUrl: `${window.location.origin}/checkout?plan=premium&canceled=true`
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create checkout session');
      }

      const { url } = await response.json();
      
      // Redirect to Stripe Checkout
      window.location.href = url;

    } catch (err) {
      console.error('Checkout error:', err);
      setError(err instanceof Error ? err.message : 'Failed to process checkout. Please try again.');
      setIsProcessing(false);
    }
  };

  useEffect(() => {
    if (searchParams.get('canceled') === 'true') {
      setError('Payment was canceled. You can try again when ready.');
    }
  }, [searchParams]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      <Navigation />

      <div className="pt-24 pb-16 px-4">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Complete Your Order</h1>
            <p className="text-base text-gray-600">
              {plan === 'free' ? 'Start using SayItBetter for free' : 'Upgrade to Premium and unlock all features'}
            </p>
          </div>

          {/* Plan Summary */}
          <div className="bg-white rounded-2xl shadow-sm p-8 mb-6">
            <div className="flex items-center justify-between mb-6 pb-6 border-b border-gray-200">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">{currentPlan.name}</h2>
                <p className="text-sm text-gray-600 mt-1">
                  {currentPlan.type === 'subscription' ? 'Monthly subscription' : 'No payment required'}
                </p>
              </div>
              <div className="text-right">
                <div className="text-3xl font-bold text-gray-900">
                  ${currentPlan.price}
                </div>
                {currentPlan.type === 'subscription' && (
                  <div className="text-sm text-gray-600">/month</div>
                )}
              </div>
            </div>

            <div className="space-y-3 mb-8">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">What's included:</h3>
              {currentPlan.features.map((feature, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <i className="ri-check-line text-xl text-green-600 mt-0.5 flex-shrink-0"></i>
                  <span className="text-sm text-gray-700">{feature}</span>
                </div>
              ))}
            </div>

            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            <button
              onClick={handleCheckout}
              disabled={isProcessing}
              className="w-full px-6 py-4 bg-blue-600 text-white text-base font-semibold rounded-full hover:bg-blue-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
            >
              {isProcessing ? (
                <span className="flex items-center justify-center space-x-2">
                  <i className="ri-loader-4-line animate-spin"></i>
                  <span>Processing...</span>
                </span>
              ) : plan === 'free' ? (
                'Start Free'
              ) : (
                'Continue to Payment'
              )}
            </button>

            {plan === 'premium' && (
              <div className="mt-4 text-center">
                <p className="text-xs text-gray-500">
                  Secure payment powered by Stripe. Cancel anytime.
                </p>
              </div>
            )}
          </div>

          {/* Security Badge */}
          <div className="flex items-center justify-center space-x-6 text-gray-400">
            <div className="flex items-center space-x-2">
              <i className="ri-lock-line text-lg"></i>
              <span className="text-xs">Secure Payment</span>
            </div>
            <div className="flex items-center space-x-2">
              <i className="ri-shield-check-line text-lg"></i>
              <span className="text-xs">SSL Encrypted</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
